import React, { useEffect, useMemo, useState } from "react";

import { getDvSourceImageUrl } from "../../../functions/themeAssets";
import {
  QUIZ_OPTION_COUNT_CHOICES,
  QUIZ_OPTION_KEYS,
  QUIZ_OPTION_LABELS,
  QUIZ_OPTION_MAX,
  applyQuizOptionCount,
  clampQuizFields,
  emptyQuizQuestion,
  getQuizOptionValues,
  inferQuizOptionCount,
  validateQuizRow,
} from "./quizHelpers";
import { QUIZ_IMAGE_HINT, validateQuizImageFile } from "./quizImageUpload";
import { QUIZ_IMAGE_DIMENSIONS, validateImageDimensions } from "./imageDimensions";

export default function QuizQuestionModal({
  open,
  mode,
  initial,
  onClose,
  onSubmit,
  isSaving,
  savingLabel,
}) {
  const [draft, setDraft] = useState(() => emptyQuizQuestion("temp"));
  const [optionCount, setOptionCount] = useState(QUIZ_OPTION_COUNT_CHOICES[0]);
  const [localError, setLocalError] = useState("");
  const [pendingImageFile, setPendingImageFile] = useState(null);
  const [pendingImagePreview, setPendingImagePreview] = useState("");

  useEffect(() => {
    if (!open) return;
    const base = initial
      ? { id: initial.id, ...clampQuizFields(initial) }
      : emptyQuizQuestion(`new-${Date.now()}`);
    const count = inferQuizOptionCount(base);
    setOptionCount(count);
    setDraft(applyQuizOptionCount(base, count));
    setPendingImageFile(null);
    setPendingImagePreview("");
    setLocalError("");
  }, [open, mode, initial]);

  useEffect(() => {
    return () => {
      if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);
    };
  }, [pendingImagePreview]);

  const savedImageUrl = useMemo(
    () => (draft.question_image ? getDvSourceImageUrl(draft.question_image) : ""),
    [draft.question_image]
  );
  const previewImageUrl = pendingImagePreview || savedImageUrl;

  if (!open) return null;

  const handleImagePick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const check = validateQuizImageFile(file);
    if (!check.ok) {
      setLocalError(check.message);
      return;
    }
    const sizeCheck = await validateImageDimensions(file, QUIZ_IMAGE_DIMENSIONS);
    if (!sizeCheck.ok) {
      setLocalError(sizeCheck.message);
      return;
    }
    setPendingImageFile(file);
    setPendingImagePreview(URL.createObjectURL(file));
    setLocalError("");
  };

  const clearPendingImage = () => {
    setPendingImageFile(null);
    setPendingImagePreview("");
  };

  const setField = (field, value, max = QUIZ_OPTION_MAX) => {
    setDraft((prev) => {
      const next = { ...prev, [field]: value.slice(0, max) };
      // Keep the chosen correct answer in sync when its option text is edited.
      if (prev.correct_answer && prev[field] === prev.correct_answer) {
        next.correct_answer = next[field].trim();
      }
      return next;
    });
    setLocalError("");
  };

  const changeOptionCount = (value) => {
    const count = Number(value);
    setOptionCount(count);
    setDraft((prev) => applyQuizOptionCount(prev, count));
    setLocalError("");
  };

  const finalize = async () => {
    setLocalError("");

    if (!pendingImageFile && !String(draft.question_image ?? "").trim()) {
      setLocalError("Please choose an image for this question.");
      return;
    }

    // Questions are image-only, so any legacy question text is dropped. A not-yet-uploaded
    // image is stood in for by "pending" so validation passes; the parent uploads the
    // file and fills in the real filename.
    const row = {
      ...draft,
      question_name: "",
      question_image: draft.question_image || "pending",
    };

    const visibleOptions = QUIZ_OPTION_KEYS.slice(0, optionCount);
    if (visibleOptions.some((key) => !String(draft[key] ?? "").trim())) {
      setLocalError(`Please fill in all ${optionCount} options.`);
      return;
    }

    const check = validateQuizRow(row, "", { requireQuestion: true });
    if (!check.ok) {
      setLocalError(check.message);
      return;
    }

    try {
      await onSubmit(
        {
          id: draft.id,
          ...check.data,
          question_name: "",
          question_image: draft.question_image,
        },
        pendingImageFile
      );
    } catch {
      /* parent surfaces error */
    }
  };

  const isEdit = mode === "edit";
  const visibleKeys = QUIZ_OPTION_KEYS.slice(0, optionCount);

  return (
    <div className="quiz-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="quiz-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quiz-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="quiz-modal-header">
          <h2 id="quiz-modal-title">{isEdit ? "Edit Question" : "New Question"}</h2>
          <button type="button" className="quiz-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="quiz-modal-body">
          {localError ? <p className="cr-alert cr-alert-error quiz-modal-error">{localError}</p> : null}

          <div className="quiz-modal-field quiz-modal-field--full">
            <label htmlFor="quiz-modal-image">Question image</label>
            <div className="quiz-modal-audio-row">
              <label className="quiz-toolbar-btn quiz-upload-label">
                <i className="fa-solid fa-upload" aria-hidden /> Choose image
                <input
                  id="quiz-modal-image"
                  type="file"
                  accept="image/jpeg,image/png,image/gif"
                  className="quiz-upload-input"
                  onChange={handleImagePick}
                  disabled={isSaving}
                />
              </label>
              {pendingImageFile ? (
                <button
                  type="button"
                  className="quiz-modal-btn quiz-modal-btn-close"
                  onClick={clearPendingImage}
                  disabled={isSaving}
                >
                  Clear selection
                </button>
              ) : null}
            </div>
            <p className="quiz-modal-limit">
              {QUIZ_IMAGE_HINT}. Players see this image as the question.
            </p>
            {previewImageUrl ? (
              <img src={previewImageUrl} alt="" className="quiz-modal-question-image" />
            ) : (
              <p className="themeupdate-muted">No image selected yet.</p>
            )}
          </div>

          <div className="quiz-modal-field quiz-modal-field--full">
            <label htmlFor="quiz-modal-option-count">Number of options</label>
            <select
              id="quiz-modal-option-count"
              className="quiz-modal-option-count"
              value={optionCount}
              onChange={(e) => changeOptionCount(e.target.value)}
              disabled={isSaving}
            >
              {QUIZ_OPTION_COUNT_CHOICES.map((n) => (
                <option key={n} value={n}>
                  {n} options
                </option>
              ))}
            </select>
          </div>

          <div className={`quiz-modal-options-grid quiz-modal-options-grid--${optionCount}`}>
            {visibleKeys.map((key, i) => (
              <div key={key} className="quiz-modal-field">
                <label htmlFor={`quiz-modal-${key}`}>{QUIZ_OPTION_LABELS[i]}</label>
                <input
                  id={`quiz-modal-${key}`}
                  type="text"
                  value={draft[key]}
                  maxLength={QUIZ_OPTION_MAX}
                  onChange={(e) => setField(key, e.target.value, QUIZ_OPTION_MAX)}
                  placeholder={QUIZ_OPTION_LABELS[i]}
                  disabled={isSaving}
                />
                <p className="quiz-modal-limit">Max length {QUIZ_OPTION_MAX} characters</p>
              </div>
            ))}
          </div>

          <div className="quiz-modal-field quiz-modal-field--full">
            <span className="quiz-modal-correct-label">Correct answer</span>
            {getQuizOptionValues(draft).length ? (
              <div className="quiz-modal-correct-options">
                {visibleKeys.map((key) => {
                  const label = String(draft[key] ?? "").trim();
                  if (!label) return null;
                  return (
                    <label key={key} className="quiz-modal-correct-option">
                      <input
                        type="radio"
                        name="quiz-correct-answer"
                        value={label}
                        checked={draft.correct_answer === label}
                        onChange={() => {
                          setDraft((prev) => ({ ...prev, correct_answer: label }));
                          setLocalError("");
                        }}
                        disabled={isSaving}
                      />
                      <span>{label}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="themeupdate-muted">Fill in the options to choose the correct answer.</p>
            )}
          </div>
        </div>

        <div className="quiz-modal-footer">
          <button
            type="button"
            className="quiz-modal-btn quiz-modal-btn-save"
            onClick={finalize}
            disabled={isSaving}
          >
            <i className="fa-solid fa-cloud-arrow-up" aria-hidden />
            {isSaving ? savingLabel || "Saving…" : isEdit ? "Update" : "Add New Question"}
          </button>
          <button type="button" className="quiz-modal-btn quiz-modal-btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
